// A small sound workshop in plain JS: seeded noise, filters, damped modes and oscillators, used by foley.ts to
// render the sounds of each period once, as samples. Pure functions on Float32Arrays (no Web Audio), so the same
// code runs in the browser and in the tests.

export type Rng = () => number;

/** A seeded random generator (mulberry32): the same seed always renders the same sound. */
export function rng(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const between = (r: Rng, a: number, b: number) => a + (b - a) * r();
export const pick = <T,>(r: Rng, xs: readonly T[]): T => xs[Math.floor(r() * xs.length) % xs.length];

export function buffer(sr: number, seconds: number): Float32Array {
  return new Float32Array(Math.max(1, Math.ceil(sr * seconds)));
}

/** Noise: white, pink (Paul Kellet's filter) or brown (leaky integrator). */
export function noise(r: Rng, color: "white" | "pink" | "brown" = "white"): () => number {
  if (color === "white") return () => r() * 2 - 1;
  if (color === "brown") {
    let last = 0;
    return () => {
      last = (last + 0.02 * (r() * 2 - 1)) / 1.02;
      return last * 3.5;
    };
  }
  let b0 = 0, b1 = 0, b2 = 0;
  return () => {
    const w = r() * 2 - 1;
    b0 = 0.99765 * b0 + w * 0.099046;
    b1 = 0.963 * b1 + w * 0.2965164;
    b2 = 0.57 * b2 + w * 1.0526913;
    return (b0 + b1 + b2 + w * 0.1848) * 0.2;
  };
}

export type FilterType = "lowpass" | "highpass" | "bandpass" | "peaking";

/** A biquad filter (Robert Bristow-Johnson's cookbook), run sample by sample; set() again to sweep it. The bandpass
 * peaks at 0 dB. */
export class Biquad {
  private b0 = 1; private b1 = 0; private b2 = 0; private a1 = 0; private a2 = 0;
  private x1 = 0; private x2 = 0; private y1 = 0; private y2 = 0;

  constructor(readonly sr: number, type: FilterType, f: number, q = 0.707, gainDb = 0) {
    this.set(type, f, q, gainDb);
  }

  set(type: FilterType, f: number, q = 0.707, gainDb = 0): this {
    const w = (2 * Math.PI * Math.min(Math.max(f, 10), this.sr * 0.45)) / this.sr;
    const cos = Math.cos(w), alpha = Math.sin(w) / (2 * q);
    let b0: number, b1: number, b2: number, a0: number;
    const a1 = -2 * cos;
    let a2: number;
    if (type === "lowpass") {
      b0 = (1 - cos) / 2; b1 = 1 - cos; b2 = b0; a0 = 1 + alpha; a2 = 1 - alpha;
    } else if (type === "highpass") {
      b0 = (1 + cos) / 2; b1 = -(1 + cos); b2 = b0; a0 = 1 + alpha; a2 = 1 - alpha;
    } else if (type === "bandpass") {
      b0 = alpha; b1 = 0; b2 = -alpha; a0 = 1 + alpha; a2 = 1 - alpha;
    } else {
      const A = 10 ** (gainDb / 40);
      b0 = 1 + alpha * A; b1 = a1; b2 = 1 - alpha * A; a0 = 1 + alpha / A; a2 = 1 - alpha / A;
    }
    this.b0 = b0 / a0; this.b1 = b1 / a0; this.b2 = b2 / a0; this.a1 = a1 / a0; this.a2 = a2 / a0;
    return this;
  }

  run(x: number): number {
    const y = this.b0 * x + this.b1 * this.x1 + this.b2 * this.x2 - this.a1 * this.y1 - this.a2 * this.y2;
    this.x2 = this.x1; this.x1 = x; this.y2 = this.y1; this.y1 = y;
    return y;
  }
}

/** A one-pole lowpass (cheap smoothing). */
export class OnePole {
  private y = 0;
  private k: number;
  constructor(sr: number, f: number) { this.k = 1 - Math.exp((-2 * Math.PI * f) / sr); }
  run(x: number): number { this.y += this.k * (x - this.y); return this.y; }
}

/** Add a decaying sine (a mode of a struck body) to out from `at` seconds: level a, e-folding time tau. */
export function mode(out: Float32Array, sr: number, at: number, f: number, a: number, tau: number, phase = 0) {
  if (f <= 0 || f >= sr * 0.48) return;
  const i0 = Math.max(0, Math.round(at * sr)), n = Math.min(out.length - i0, Math.ceil(tau * sr * 7));
  const w = (2 * Math.PI * f) / sr, k = Math.exp(-1 / (tau * sr));
  const c = k * Math.cos(w), s = k * Math.sin(w);
  let re = a * Math.cos(phase), im = a * Math.sin(phase);
  for (let i = 0; i < n; i++) {
    out[i0 + i] += im;
    const nr = re * c - im * s;
    im = re * s + im * c;
    re = nr;
  }
}

/** Add a burst of filtered noise from `at`: sharp attack, exponential decay tau (filter built by the caller). */
export function burst(out: Float32Array, sr: number, at: number, a: number, tau: number, src: () => number,
  filter?: { run(x: number): number }, attack = 0.0004) {
  const i0 = Math.max(0, Math.round(at * sr)), n = Math.min(out.length - i0, Math.ceil((tau * 7 + attack) * sr));
  const na = Math.max(1, Math.round(attack * sr)), k = Math.exp(-1 / (tau * sr));
  let e = 1;
  for (let i = 0; i < n; i++) {
    const x = filter ? filter.run(src()) : src();
    const g = i < na ? i / na : (e *= k);
    out[i0 + i] += a * g * x;
  }
}

/** Band-limited sawtooth and square (PolyBLEP), for voices and brass. */
export class Osc {
  private p = 0;
  constructor(readonly sr: number, phase = 0) { this.p = phase; }
  private blep(t: number, dt: number): number {
    if (t < dt) { t /= dt; return t + t - t * t - 1; }
    if (t > 1 - dt) { t = (t - 1) / dt; return t * t + t + t + 1; }
    return 0;
  }
  /** Advance by one sample at frequency f; returns [saw, square]. */
  next(f: number): [number, number] {
    const dt = Math.min(0.45, f / this.sr);
    this.p += dt;
    if (this.p >= 1) this.p -= 1;
    const saw = 2 * this.p - 1 - this.blep(this.p, dt);
    let q = this.p < 0.5 ? 1 : -1;
    q += this.blep(this.p, dt);
    let t2 = this.p + 0.5;
    if (t2 >= 1) t2 -= 1;
    q -= this.blep(t2, dt);
    return [saw, q];
  }
}

export const smoothstep = (a: number, b: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

/** A smooth hump: 1 at c, falling off with width w (wider before c when rise > 1). */
export function hump(t: number, c: number, w: number, rise = 1): number {
  const d = (t - c) / (t < c ? w * rise : w);
  return Math.exp(-d * d);
}

/** Scale channels so the loudest sample is `peak`. */
export function normalize(chs: Float32Array[], peak = 0.9): Float32Array[] {
  let m = 0;
  for (const c of chs) for (let i = 0; i < c.length; i++) m = Math.max(m, Math.abs(c[i]));
  if (m > 0) for (const c of chs) for (let i = 0; i < c.length; i++) c[i] *= peak / m;
  return chs;
}

/** Fade the last `seconds` to silence (no click when a sample is cut short). */
export function fadeOut(c: Float32Array, sr: number, seconds: number) {
  const n = Math.min(c.length, Math.round(seconds * sr));
  for (let i = 0; i < n; i++) c[c.length - 1 - i] *= i / n;
}

/** Make a sample loop seamlessly: crossfade its last `seconds` into its start, then drop them. */
export function loopable(c: Float32Array, sr: number, seconds: number): Float32Array {
  const n = Math.round(seconds * sr), len = c.length - n;
  const out = c.slice(0, len);
  for (let i = 0; i < n; i++) {
    const k = i / n;
    out[i] = c[i] * Math.sqrt(k) + c[len + i] * Math.sqrt(1 - k);
  }
  return out;
}

/** Equal-power pan of a mono sample point into a stereo pair (p: −1 left … 1 right). */
export function panGains(p: number): [number, number] {
  const a = ((Math.max(-1, Math.min(1, p)) + 1) * Math.PI) / 4;
  return [Math.cos(a), Math.sin(a)];
}
